import { useState, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { transition, variants } from '../variants';
import ImageCropper from './ImageCropper';
import { useWindowSize } from '../../../../../utils/hooks/useWindowSize';
import { useContainerSize } from '../../../../../utils/hooks/useContainerSize';
import { useClickOutside } from '../../../../../utils/hooks/useClickOutside';
import ColorPicker from '../../../Style/ColorPicker';
import styles from './QrConnectHeader.module.css';

export default function QrConnectHeader(props) {

    const { header, onHeaderChange } = props;
    const { width: windowWidth } = useWindowSize();
    const [ containerRef, { width: containerWidth }] = useContainerSize();

    const hideText = useMemo(() => header.hide_text, [header.hide_text]);
    const useImage = useMemo(() => header.use_image, [header.use_image]);

    const [ expanded, setExpanded ] = useState(false);

    const handleHeaderChange = (newHeader) => {
        // If an image is being set and hide_text isn't already true, toggle it on
        if (newHeader.image && !newHeader.hide_text && newHeader.image !== header.image) {
            onHeaderChange({ ...newHeader, hide_text: true });
        } else {
            onHeaderChange(newHeader);
        }
    };

    return (
        <div className={styles.container} ref={containerRef}>
            <div className={styles.header}>
                <h5>Header</h5>
            </div>

            <div className={styles.rowWrapper}>
                <AnimatePresence mode='wait' initial={false}>
                    {((containerWidth > 470 && windowWidth > 768) || expanded) ? (
                        <motion.div
                            key='input'
                            className={styles.rowContainer}
                            transition={transition}
                            variants={variants}
                            initial='initial'
                            animate='animate'
                            exit='initial'
                        >
                            <AnimatePresence initial={false}>
                            {(containerWidth <= 470 || windowWidth <= 768) && (
                                <motion.button 
                                    className={styles.collapseBtn} 
                                    onClick={() => setExpanded(false)}
                                    transition={transition}
                                    variants={variants}
                                    initial='initial'
                                    animate='animate'
                                    exit='initial'
                                >
                                    <i className="far fa-chevron-up" />
                                </motion.button>
                            )}
                            </AnimatePresence>
                            
                            <div className={styles.rowContent}>
                                <div className={styles.rowPreview}>
                                    <input
                                        className={styles.previewInput}
                                        type='text'
                                        value={!header.hide_text ? header.text || '' : ''}
                                        onChange={(e) => handleHeaderChange({ ...header, text: e.target.value })}
                                        maxLength='200'
                                        style={{
                                            color: header.color,
                                            backgroundColor: header.background,
                                            backgroundImage: useImage ? `url(${header.image})` : null,
                                            textAlign: 'center',
                                            fontSize: '2rem',
                                            padding: '2rem',
                                        }}
                                        placeholder=""
                                        disabled={header.hide_text}
                                    />
                                    {!header.hide_text && <span className={styles.headerText} style={{color: header.color}}>Header</span>}
                                </div>
                                {/* Controls (Background & Text Color) */}
                                <div className={styles.colorControls}>
                                    <PickerControl 
                                        currentColor={header.background} 
                                        onChange={(val) => handleHeaderChange({ ...header, background: val })}
                                        title="Background" 
                                        type="bg" 
                                    />
                                    <PickerControl 
                                        currentColor={header.color} 
                                        onChange={(val) => handleHeaderChange({ ...header, color: val })}
                                        title="Text Color" 
                                        type="text" 
                                    />
                                </div>
                            </div>
                            <div className={styles.toggles}>
                                <label className={styles.toggleWrap}>
                                    <div className={styles.toggleContainer}>
                                        <span className={styles.toggle} />
                                    </div>
                                    <input type="checkbox" checked={useImage} onChange={e => handleHeaderChange({ ...header, use_image: e.target.checked })} />
                                    Use Background Image
                                </label>
                                <label className={styles.toggleWrap}>
                                    <div className={styles.toggleContainer}>
                                        <span className={styles.toggle} />
                                    </div>
                                    <input type='checkbox' checked={hideText} onChange={e => handleHeaderChange({ ...header, hide_text: e.target.checked })} />
                                    Hide Header Text
                                </label>
                            </div>
                            <AnimatePresence initial={false}>
                                {useImage && (
                                    <motion.div
                                        className={styles.cropWrapper}
                                        transition={transition}
                                        variants={variants}
                                        initial='initial'
                                        animate='animate'
                                        exit='initial'
                                    >
                                        <ImageCropper containerWidth={containerWidth} onHeaderChange={handleHeaderChange} header={header} />
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </motion.div>
                    ) : (
                        <motion.button
                            key='button'
                            className={styles.previewButton}
                            onClick={() => setExpanded(true)}
                            disabled={containerWidth > 470 && windowWidth > 768}
                            transition={transition}
                            variants={variants}
                            initial='initial'
                            animate='animate'
                            exit='initial'
                            style={{
                                backgroundImage: useImage ? `url(${header.image})` : null,
                                backgroundColor: header.background, 
                                color: header.color
                            }}
                        >
                            {!hideText && header.text || 'Header'}
                        </motion.button>
                    )}
                </AnimatePresence>
            </div>
        </div>
    );
};

const PickerControl = ({ currentColor, onChange, title, type }) => {
    const btnRef = useRef(null);
    const [ ref, isOpen, setIsOpen ] = useClickOutside(false, btnRef);
  
    return (
        <div ref={ref} style={{ position: 'relative', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <button
                type='button'
                ref={btnRef}
                className={styles.colorButton}
                onClick={() => setIsOpen(!isOpen)}
                style={{ backgroundColor: currentColor }}
                title={title}
            />
            {isOpen && (
                <div className={styles.pickerPortal}>
                    <ColorPicker color={currentColor} onChange={onChange} name={type} />
                </div>
            )}
        </div>
    );
};